// Auto-detect host: production uses IP/hostname, dev uses localhost
// Override with VITE_API_URL env var when needed (e.g. mobile emulator)
const API_HOST =
  typeof window !== 'undefined' && window.location?.hostname
    ? window.location.hostname
    : 'localhost';

// An empty VITE_API_URL is a deliberate setting, not an absent one: it means
// "use relative paths" so calls go through whatever proxy is serving the app
// (nginx /api/ in the prod image). `||` collapsed that into the :8080 guess,
// which no prod deployment listens on. Only an undefined value falls back.
const configuredApiUrl = import.meta.env.VITE_API_URL;

export const BASE_URL =
  configuredApiUrl === undefined ? `http://${API_HOST}:8080` : configuredApiUrl;

// Normal application API requests are allowed up to five minutes. Individual
// UI timers (such as polling cadence) and auth/health-specific behavior remain
// separate from this transport timeout.
export const API_REQUEST_TIMEOUT_MS = 5 * 60 * 1000;

export const API_URLs = {
  // Auth
  register: `${BASE_URL}/api/v1/users/register`,
  login: `${BASE_URL}/api/v1/users/login`,
  refreshToken: `${BASE_URL}/api/v1/users/refresh-token`,
  logout: `${BASE_URL}/api/v1/users/logout`,
  currentUser: `${BASE_URL}/api/v1/users/current-user`,
  // A signed-in user changes their own password (TASK-2026-10-12-002).
  changePassword: `${BASE_URL}/api/v1/users/change-password`,
  // Forgot Password (TASK-2026-10-10-001) — DISABLED by TASK-2026-10-12-002;
  // the backend routes answer 404. Uncomment with the backend routes to restore.
  // forgotPassword: `${BASE_URL}/api/v1/users/forgot-password`,
  // verifyOtp: `${BASE_URL}/api/v1/users/verify-otp`,
  // resetPassword: `${BASE_URL}/api/v1/users/reset-password`,
  users: `${BASE_URL}/api/v1/users`,
  // Microsoft Entra ID SSO (TASK-2026-10-13-001). ssoLogin is navigated to as
  // a full page, never called with XHR: it 302s the browser to Microsoft.
  ssoConfig: `${BASE_URL}/api/v1/auth/sso/config`,
  ssoLogin: `${BASE_URL}/api/v1/auth/sso/login`,
  cronJobHistory: `${BASE_URL}/api/v1/cron-job-history`,

  // SIOP source workbooks — upload, per-sheet ingest configuration, rollback.
  // Its own AUTHENTICATED mount, deliberately not under /siop (which is public
  // by design): an upload here replaces what the whole cockpit reads.
  // See backend routes/siop/dataSources.routes.js (TASK-2026-09-23-001).
  siopDataSources: `${BASE_URL}/api/v1/siop-data-sources`,
  fabricDataUpdateTimes: `${BASE_URL}/api/v1/fabric-data-update-times`,

  // Permissions
  getCurrentUserPermissions: `${BASE_URL}/api/v1/users/get-user-permissions`,

  // Health
  healthcheck: `${BASE_URL}/api/v1/healthcheck`,

  // SIOP — Chemelex Agentic SIOP demand forecasting
  siopKpis: `${BASE_URL}/api/v1/siop/kpis`,
  siopPivot: `${BASE_URL}/api/v1/siop/pivot`,
  siopVariance: `${BASE_URL}/api/v1/siop/variance`,
  siopAopClosure: `${BASE_URL}/api/v1/siop/aop-closure`,
  siopScZones: `${BASE_URL}/api/v1/siop/sc-zones`,
  siopScorecard: `${BASE_URL}/api/v1/siop/scorecard`,
  siopAsk: `${BASE_URL}/api/v1/siop/ai/ask`,
  siopForecastRun: `${BASE_URL}/api/v1/siop/forecast/run`,
  siopForecast: `${BASE_URL}/api/v1/siop/forecast`,
  siopOverride: `${BASE_URL}/api/v1/siop/override`,
  siopAlerts: `${BASE_URL}/api/v1/siop/alerts`,
  siopStdCost: `${BASE_URL}/api/v1/siop/std-cost`,
  siopLookup: `${BASE_URL}/api/v1/siop/lookup`,
  siopDataReload: `${BASE_URL}/api/v1/siop/data/reload`,
  siopDataStatus: `${BASE_URL}/api/v1/siop/data/status`,
  siopFilters: `${BASE_URL}/api/v1/siop/filters`,

  // Pulse Bridge — demand-bridge waterfall (own top-level mount, see
  // backend pulseBridge.routes.js for why it isn't nested under /siop).
  pulseBridgeStatus: `${BASE_URL}/api/v1/pulse-bridge/status`,
  pulseBridgeUpload: `${BASE_URL}/api/v1/pulse-bridge/upload`,
  pulseBridgeRegionUpload: `${BASE_URL}/api/v1/pulse-bridge/region-upload`,
  pulseBridgeRegionMapping: `${BASE_URL}/api/v1/pulse-bridge/region-mapping`,
  pulseBridgeSheets: `${BASE_URL}/api/v1/pulse-bridge/sheets`,
  pulseBridgeRegionSheets: `${BASE_URL}/api/v1/pulse-bridge/region-sheets`,
  pulseBridgeLookupColumns: `${BASE_URL}/api/v1/pulse-bridge/lookup-columns`,
  pulseBridgeMapping: `${BASE_URL}/api/v1/pulse-bridge/mapping`,
  pulseBridgePlan: `${BASE_URL}/api/v1/pulse-bridge/plan`,
  pulseBridgeData: `${BASE_URL}/api/v1/pulse-bridge/data`,
  // Forecasting Analysis — served by the backend's forecast-analysis client
  // (a mock today; the data science team's API replaces it inside the backend).
  forecastAnalysisMeta: `${BASE_URL}/api/v1/forecast-analysis/meta`,
  forecastAnalysisLines: `${BASE_URL}/api/v1/forecast-analysis/lines`,
  forecastAnalysisAsk: `${BASE_URL}/api/v1/forecast-analysis/ask`,
  forecastAnalysisScenarios: `${BASE_URL}/api/v1/forecast-analysis/scenarios`,
  // Daily Pulse — sample data (meta.source 'mock') until the daily feed lands.
  pulseBridgeDaily: `${BASE_URL}/api/v1/pulse-bridge/daily`,
  pulseBridgeDailySales: `${BASE_URL}/api/v1/pulse-bridge/daily/sales`,
  // The same read as /data, as an .xlsx — one tab per bar of the bridge.
  pulseBridgeExport: `${BASE_URL}/api/v1/pulse-bridge/export`,
  pulseBridgeInsights: `${BASE_URL}/api/v1/pulse-bridge/insights`,
  pulseBridgeAsk: `${BASE_URL}/api/v1/pulse-bridge/ask`,
  chemelexInsightBoard: `${BASE_URL}/api/v1/pulse-bridge/chemelex-insights`,
  chemelexInsightAsk: `${BASE_URL}/api/v1/pulse-bridge/chemelex-ask`,
  chemelexInsightQuestions: `${BASE_URL}/api/v1/pulse-bridge/chemelex-questions`,
  chemelexInsightQuestion: (questionNumber) =>
    `${BASE_URL}/api/v1/pulse-bridge/chemelex-questions/${questionNumber}`,
  pulseBridgeAccrual: `${BASE_URL}/api/v1/pulse-bridge/accrual`,
  pulseBridgeComments: `${BASE_URL}/api/v1/pulse-bridge/comments`,
  pulseBridgeFuturePullIn: `${BASE_URL}/api/v1/pulse-bridge/future-pull-in`,
  pulseBridgeRegionalFigures: `${BASE_URL}/api/v1/pulse-bridge/regional-figures`,
  // The four hand-entered figures as one grid per period — the workspace's
  // Manual Figures dialog.
  pulseBridgeManualFigures: `${BASE_URL}/api/v1/pulse-bridge/manual-figures`,
  // Order Rate Logic (X) — the config the business edits in-app, replacing the
  // uploaded forecast workbook. Read/written per calendar month.
  pulseBridgeOrderRate: `${BASE_URL}/api/v1/pulse-bridge/order-rate`,
  pulseBridgeOrderRateMonths: `${BASE_URL}/api/v1/pulse-bridge/order-rate/months`,
};
